import React, { useCallback, useEffect, useRef } from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  arrow,
  size,
  type Placement,
} from '@floating-ui/react';
import { clsx } from 'clsx';
import { XMarkIcon } from '@heroicons/react/24/outline';

/**
 * Tabbable selector — kept loose enough to cover what we render here (buttons,
 * the close affordance). Excludes `[tabindex="-1"]` and disabled controls.
 */
const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), ' +
  'select:not([disabled]), textarea:not([disabled]), ' +
  '[tabindex]:not([tabindex="-1"])';

const getFocusable = (root: HTMLElement | null): HTMLElement[] => {
  if (!root) return [];
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute('disabled'),
  );
};

interface Props {
  /** ARIA labelling/description ids — shared across compact and split. */
  titleId: string;
  bodyId: string;
  stepNumber: number;
  totalSteps: number;
  /** Anchor target. `null` → centered card (welcome / final / no-target). */
  targetEl: HTMLElement | null;
  /** Optional second element. When set, the card anchors to the bounding
   * union of `targetEl` and this — keeps the card vertically centered on
   * the full spotlight area (e.g. trigger row + open popover). */
  secondaryEl?: HTMLElement | null;
  placement?: import('../types').TourPlacement;
  isFirst: boolean;
  isLast: boolean;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
  onRemindLater?: () => void;
  hideRemindLater?: boolean;
  /** Width-class override per layout. Compact: `w-[400px]`. Split: `w-[640px]`. */
  widthClassName: string;
  /** Body content — Compact / Split fill this with their own layout. */
  children: React.ReactNode;
  /** Forwarded for the primary CTA focus management. */
  primaryCtaRef?: React.MutableRefObject<HTMLButtonElement | null>;
}

/**
 * Shared chrome for both compact and split tour cards. Owns:
 *   - Floating UI positioning + arrow
 *   - Progress bar
 *   - Header row (step counter + Skip — currently text, becomes [×] icon in Task 6)
 *   - Action row (Remind me later? / Back / Next)
 *   - Focus trap + focus restoration
 *   - aria-modal flip per anchored vs. centered
 *
 * The body region is filled with `children` so each layout (compact / split)
 * can render its own internal structure (title, body, media, etc.).
 *
 * This is a pure refactor in Task 3 — visually identical to the previous
 * inline TourCard. Tasks 6 (Skip → ×) and 7 (Remind gating) modify it.
 */
export const TourCardChrome: React.FC<Props> = ({
  titleId,
  bodyId,
  stepNumber,
  totalSteps,
  targetEl,
  secondaryEl,
  placement: placementProp,
  isFirst,
  isLast,
  onNext,
  onBack,
  onSkip,
  onRemindLater,
  hideRemindLater,
  widthClassName,
  children,
  primaryCtaRef,
}) => {
  const arrowRef = useRef<HTMLDivElement | null>(null);
  const fallbackCtaRef = useRef<HTMLButtonElement | null>(null);
  const ctaRef = primaryCtaRef ?? fallbackCtaRef;
  const cardRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocusedRef = useRef<Element | null>(null);

  const { refs, floatingStyles, placement, middlewareData } = useFloating({
    placement: (placementProp ?? 'bottom') as Placement,
    // Tour anchors are commonly `position: fixed` (right-edge filter
    // sidebar, modal headers). Matching the floating strategy keeps
    // positioning relative to the viewport instead of the offsetParent —
    // which avoids subtle drift when the body is narrower than the
    // viewport (e.g. scrollbar) or when a scroll repositions the offsetParent.
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(12),
      flip(),
      shift({ padding: 12 }),
      // Cap card to the visible viewport (minus 12px gutters) so a tall
      // split-layout card on a short viewport can't push the action row
      // (Back / Next / Skip) below the fold. The body slot has its own
      // scroll container so content overflow is recoverable; without
      // this clamp the card itself would simply grow off-screen.
      size({
        padding: 12,
        apply({ availableWidth, availableHeight, elements }) {
          // jsdom (used by unit tests) reports 0×0 viewports — applying
          // those would zero the card. Skip when measurements are absent.
          if (availableWidth <= 0 || availableHeight <= 0) return;
          Object.assign(elements.floating.style, {
            maxWidth: `${availableWidth}px`,
            maxHeight: `${availableHeight}px`,
          });
        },
      }),
      arrow({ element: arrowRef }),
    ],
  });

  const setCardRef = useCallback(
    (node: HTMLDivElement | null) => {
      cardRef.current = node;
      refs.setFloating(node);
    },
    [refs],
  );

  useEffect(() => {
    if (!targetEl) {
      refs.setReference(null);
      return;
    }
    // Always anchor through a virtual reference that re-reads each rect at
    // call time. A previous frame's `secondarySelector` element can still be
    // referenced for a render after it's been detached — its rect collapses
    // to (0,0,0,0), the union slumps to the viewport origin, and the card
    // jumps off-screen. The `isConnected` / zero-size guard treats a missing
    // secondary as "primary only" so the transition is invisible.
    refs.setReference({
      getBoundingClientRect: () => {
        const a = targetEl.getBoundingClientRect();
        if (!secondaryEl || !secondaryEl.isConnected) return a;
        const b = secondaryEl.getBoundingClientRect();
        if (b.width === 0 && b.height === 0) return a;
        const left = Math.min(a.left, b.left);
        const top = Math.min(a.top, b.top);
        const right = Math.max(a.right, b.right);
        const bottom = Math.max(a.bottom, b.bottom);
        return new DOMRect(left, top, right - left, bottom - top);
      },
    });
  }, [targetEl, secondaryEl, refs]);

  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement;
    return () => {
      const target = previouslyFocusedRef.current;
      if (target instanceof HTMLElement && document.contains(target)) {
        target.focus();
      }
    };
  }, []);

  // Focus the primary CTA on mount and on step transition (the consumer
  // owns the dependency list by passing primaryCtaRef; the chrome itself
  // does the initial focus). Keep behaviour byte-compatible with the
  // pre-refactor TourCard: focus the CTA on mount.
  useEffect(() => {
    ctaRef.current?.focus();
  }, [ctaRef]);

  // Focus trap: while the card is mounted, Tab and Shift+Tab cycle within
  // it. The listener lives on `document` (not on `cardRef.current`) so a
  // post-mount ref reassignment can't strand the handler on a detached
  // node — we read the live ref inside the handler. Phase guarded by an
  // `isConnected` check on the captured node.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const node = cardRef.current;
      if (!node || !node.isConnected) return;
      const focusable = getFocusable(node);
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;
      // Outside the card → snap focus back inside. Without this, Tab from
      // the spotlit anchor (which lives outside the trap) would walk away
      // into the rest of the page.
      if (!active || !node.contains(active)) {
        e.preventDefault();
        first.focus();
        return;
      }
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const positioned = targetEl !== null;

  const arrowSide: Record<string, string> = {
    top: 'bottom',
    bottom: 'top',
    left: 'right',
    right: 'left',
  };
  const staticSide = arrowSide[placement.split('-')[0]];

  return (
    <div
      ref={setCardRef}
      role="dialog"
      aria-modal={positioned ? 'false' : 'true'}
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      className={clsx(
        'z-[10002] flex flex-col rounded-lg bg-white p-5 shadow-2xl ring-1 ring-gray-900/10',
        widthClassName,
        // Centered-mode (welcome / final / no anchor) doesn't go through
        // Floating UI's size middleware, so cap with viewport units here.
        // Use dvh so mobile browsers' dynamic toolbars don't push the card
        // off-screen.
        !positioned &&
          'fixed left-1/2 top-1/2 max-h-[calc(100dvh-24px)] max-w-[calc(100vw-24px)] -translate-x-1/2 -translate-y-1/2',
      )}
      style={positioned ? floatingStyles : undefined}
    >
      {positioned && middlewareData.arrow && staticSide && (
        <div
          ref={arrowRef}
          className="absolute h-3 w-3 rotate-45 bg-white"
          style={{
            left: middlewareData.arrow.x != null ? `${middlewareData.arrow.x}px` : '',
            top: middlewareData.arrow.y != null ? `${middlewareData.arrow.y}px` : '',
            [staticSide]: '-6px',
          }}
        />
      )}

      {/* Header row: step counter (left) + [×] close icon (right). The
          icon button keeps the Skip path: same onClick, same Esc keybind
          in TourProvider. Conventional close affordance per RESEARCH §2.
          Progress is communicated through the "Step N of M" counter; the
          original top-edge progress bar was removed because at 1px tall
          on a rounded card it read as a rendering glitch rather than a
          deliberate cue. shrink-0 keeps the header pinned when the body
          scrolls under a viewport-capped card. */}
      <div className="flex shrink-0 items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
          Step {stepNumber} of {totalSteps}
        </span>
        <button
          type="button"
          onClick={onSkip}
          aria-label="Skip tour"
          className="-m-1 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
        >
          <XMarkIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      {/* Body slot — compact / split fill this with their own layout.
          When the card is height-capped by the size middleware (or by the
          centered-mode max-h class), this is the region that scrolls so
          the action row below stays reachable. min-h-0 is required for
          the flex child to actually allow overflow. */}
      <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>

      {/* Action row split into two zones:
          - Left: deferral affordances (Remind me later — only visible in
            whats-new mode). The × icon at the top-right is the primary
            exit; this slot is for explicit deferral, not abandonment.
          - Right: navigation (Back / Next). Primary forward action lives
            at the rightmost edge — conventional reading-order anchor.
          The split (`justify-between`) prevents Remind from competing with
          Back/Next. When Remind is hidden, an empty `<span>` keeps the
          right-side navigation pinned to the right via the same flex rule. */}
      <div className="mt-4 flex shrink-0 items-center justify-between gap-2">
        <div className="flex items-center">
          {onRemindLater && !hideRemindLater && (
            <button
              type="button"
              onClick={onRemindLater}
              className="rounded-md px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100"
            >
              Remind me later
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isFirst && (
            <button
              type="button"
              onClick={onBack}
              className="rounded-md px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100"
            >
              Back
            </button>
          )}
          <button
            ref={ctaRef}
            type="button"
            onClick={onNext}
            className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-primary-700"
          >
            {isLast ? 'Done' : isFirst ? 'Take a tour →' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
};
